/**
 * Dashboard API Schema
 *
 * Zod contract for GET /api/v1/dashboard (apps/edge-api/src/routes/dashboard.ts).
 * Mirrors the actual `responseData` shape built by the route — not an aspirational
 * shape. Sections are role-scoped: `adminOps` (admin/owner) and `supervisorData`
 * (supervisor/admin/owner) are `.optional()` because the route only populates
 * them when the requester's role qualifies (see `canViewAdminOps` /
 * `canViewSupervisorData` in the route).
 *
 * `.passthrough()` is intentionally NOT used — every field the backend sends is
 * described so schema drift (missing/renamed/retyped field) fails validation
 * instead of silently propagating a NaN/undefined into the UI (WS1-T7).
 */

import { z } from 'zod/v4';
import { alertSeveritySchema } from './alerts';
import { lockedDemoOnlySchema } from './common';
import { processingErrorReasonSchema } from './service-status';

// ============================================================================
// Shared
// ============================================================================

export const dashboardPeriodSchema = z.enum(['today', 'week', 'month', 'quarter']);

/** `{ value, trend }` pair shared by qualityScore/saleProbability. */
const metricWithTrendSchema = z.object({
  value: z.number().nullable(),
  trend: z.number().nullable(),
});

// ============================================================================
// Top-level sections
// ============================================================================

export const dashboardBudgetSchema = z
  .object({
    current: z.number(),
    limit: z.number(),
    // Nullable: undefined (not 0%) when limit <= 0 — no monthly limit set
    // means "% used" has no meaning, not a fabricated ratio (BudgetRepository
    // divides by zero otherwise).
    percentUsed: z.number().nullable(),
    remaining: z.number(),
  })
  .nullable();

export const dashboardPipelineStatsSchema = z.object({
  pending: z.number().int().nonnegative(),
  processing: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  completedToday: z.number().int().nonnegative(),
});

/**
 * Structured record title (WS6-T1) — the backend no longer composes an
 * English "Call 12345" / "Chat #a1b2c3d4" string; the frontend assembles
 * and localizes the display string from these parts: direction and
 * counterparty > externalId > idPrefix, with `type` picking the label (#1104).
 *
 * `crmActivityName` stays for clients deployed before #1104 and must NOT go
 * back to the top of the priority: it is the CRM's own title, written in the
 * portal's language, not the interface's.
 */
export const dashboardRecentRecordTitleSchema = z.object({
  type: z.enum(['call', 'chat']),
  crmActivityName: z.string().nullable(),
  externalId: z.string().nullable(),
  idPrefix: z.string(),
  // Who the conversation was with and which way the call went (#1104): the
  // widget names a record in the interface language — "Outgoing call · Olena"
  // — instead of the CRM activity's own title, which is in the portal's
  // language. Optional: a server deployed before the client omits them.
  callDirection: z.enum(['inbound', 'outbound']).nullable().optional(),
  participantName: z.string().nullable().optional(),
  /** Only when there is no `participantName` — the widget shows one or the other. */
  participantPhone: z.string().nullable().optional(),
});

export const dashboardRecentRecordSchema = z.object({
  id: z.string(),
  title: dashboardRecentRecordTitleSchema,
  // Raw records_metadata.status (record-status-sets.ts SSOT) — not a closed
  // enum here, see task-7-report.md for why recordStatusSchema isn't reused.
  status: z.string(),
  // Raw ISO createdAt, or null when missing (WS6-T1) — never a
  // backend-formatted "5 mins ago"/"Unknown" string; the frontend computes
  // relative time (lib/format-relative.ts) or renders "—" for null.
  timestamp: z.string().nullable(),
});

export const dashboardPrimaryAlertEvidenceSchema = z.object({
  quote: z.string(),
  timecode: z.string(),
  startSeconds: z.number().int().nonnegative(),
  speaker: z.enum(['agent', 'client', 'system']),
});

export const dashboardRecentAlertSchema = z.object({
  id: z.string(),
  title: z.string(),
  severity: alertSeveritySchema,
  timestamp: z.string(),
  /**
   * Absent for a narrowed access grant without `records.read` (E1.5, #1336):
   * the alert stays a quality row, without a way into the record. No widget
   * reads it today.
   */
  recordId: z.string().optional(),
  agentName: z.string().nullable(),
  /**
   * Transcript-grounded quote for the Priority Alerts surface.
   *
   * Optional on purpose: `recentAlerts` also feeds the admin/owner "Recent
   * Alerts" panels, which show every alert. Alerts whose evidence carries no
   * parseable timecode (compliance/quality rows, legacy events) still belong
   * in the list — they simply render without a quote.
   */
  evidence: dashboardPrimaryAlertEvidenceSchema.optional(),
});

export const dashboardIssueSchema = z.object({
  id: z.string(),
  type: z.string(),
  message: z.string(),
});

export const dashboardRecordsChartItemSchema = z.object({
  day: z.string(),
  records: z.number().int().nonnegative(),
});

/**
 * Daily average quality score over the window (WS7-T14) — the real-date
 * sibling of `sparklines.qualityScore` (a bare number[]). Exposing the ISO
 * `day` alongside each value lets the trend chart render a real calendar axis
 * (instead of positional "Day N" labels) and anchor config-change markers to
 * the correct tick. `value` is null for a day with no scored calls — the chart
 * must break the line there, never plot a fabricated zero.
 */
export const dashboardQualityTrendItemSchema = z.object({
  day: z.string(),
  value: z.number().nullable(),
});

export const dashboardCostChartItemSchema = z.object({
  provider: z.string(),
  cost: z.number(),
  count: z.number().int().nonnegative(),
});

/**
 * Daily customer revenue (WS5-T4) — the sum of usage_charges (what customers
 * were billed) per calendar day, already converted dollars -> cents (see
 * UsageChargeRepository.getRevenueChart / dollarsToCents). Independent of
 * costChart above (COGS/`costs` table) — this is the revenue-basis sibling,
 * feeding the owner dashboard's real "Revenue" panel.
 */
export const dashboardRevenueChartItemSchema = z.object({
  day: z.string(),
  revenueCents: z.number().nonnegative(),
});

/**
 * Records chart broken down by type (WS5-T4) — day x type ('call'/'chat')
 * counts, backing the owner dashboard's All/Calls/Chats toggle on the
 * records panel. Independent of recordsChart above (flat day-only counts,
 * still used for the "All" slice; its per-day `completed`/`analyzed` columns
 * feed sparklines.recordsProcessed / sparklines.callsAnalyzed).
 */
export const dashboardRecordsChartByTypeItemSchema = z.object({
  day: z.string(),
  type: z.string(),
  count: z.number().int().nonnegative(),
});

/**
 * Cost broken down by cost_type + provider (WS5-T3), with ASR minutes
 * attached. Independent of `costChart` above (which groups by provider only,
 * for the "Budget by Provider" panel) — this field exists so the owner
 * dashboard can classify ASR vs LLM by the real `costType` column instead of
 * pattern-matching a provider name string.
 */
export const dashboardCostBreakdownItemSchema = z.object({
  costType: z.string(),
  provider: z.string(),
  totalCents: z.number(),
  count: z.number().int().nonnegative(),
  totalMinutes: z.number().nonnegative(),
});

/**
 * A narrowed access grant (E1.5, #1336) gets only the metrics of the
 * aggregates it covers: `recordsProcessed` and `activeProjects` need
 * `aggregates.flow`, `issuesFound` needs `aggregates.quality` — absent
 * otherwise, hence optional. The two score metrics are always sent; without
 * their aggregate they are `{ value: null, trend: null }` («no score»).
 */
export const dashboardMetricsSchema = z.object({
  qualityScore: metricWithTrendSchema,
  saleProbability: metricWithTrendSchema,
  recordsProcessed: z
    .object({
      value: z.number().int().nonnegative(),
      today: z.number().int().nonnegative(),
      // Percent delta vs the prev period (same [start-(end-start), start) window
      // as qualityScore/saleProbability above), null when
      // there's nothing to divide by (prev === 0) — WS3-T4.
      trend: z.number().nullable(),
      // Raw prev-period completed count backing `trend` above — exposed
      // alongside it so a consumer can call the ui-worker `buildTrend(value,
      // prev, ...)` SSOT directly instead of re-deriving prev from a percent
      // (WS3-T4).
      prev: z.number().int().nonnegative(),
    })
    .optional(),
  // Records with a current analysis in the period (#822) — what a card
  // labelled "analyzed" must show. `recordsProcessed` above counts terminal
  // statuses, which missed and short calls reach without any analysis.
  // value/prev are sums of the same per-day series as
  // sparklines.callsAnalyzed, so the number, its delta and its sparkline
  // share one window and one agent filter. `prev` (and so `trend`) is null
  // when the prev-window fetch failed; the whole field is absent when the
  // current one did. `.optional()` also tolerates cache entries written
  // before this field existed.
  callsAnalyzed: z
    .object({
      value: z.number().int().nonnegative(),
      trend: z.number().nullable(),
      prev: z.number().int().nonnegative().nullable(),
    })
    .optional(),
  activeProjects: z.object({ value: z.number().int().nonnegative() }).optional(),
  issuesFound: z
    .object({
      value: z.number().int().nonnegative(),
      // See recordsProcessed.trend/.prev above for the shape contract. Note:
      // `value` here counts ALL unresolved alerts (unwindowed, pre-existing
      // behavior) — a different measure from `trend`/`prev` by design (fix
      // round 1, review finding): `trend` is a windowed inflow delta (alerts
      // *triggered* in the current window vs the prev window), and `prev` is
      // that prev-window trigger count. Diffing the unwindowed `value` against
      // a windowed prev produced nonsensical percentages (e.g. +1900%) that
      // also swung purely from switching the period selector — see
      // task-4-report.md.
      trend: z.number().nullable(),
      prev: z.number().int().nonnegative(),
    })
    .optional(),
  // Current-period total cost across providers (cents), derived by summing
  // costChart — no extra query. `costTrend`/`prevCostCents` follow the same
  // shape contract as recordsProcessed above (WS3-T4). Provider COGS: real
  // values only for the platform owner (#1148); for everyone else the fields
  // are absent (#1177).
  totalCostCents: z.number().nonnegative().optional(),
  costTrend: z.number().nullable().optional(),
  prevCostCents: z.number().nonnegative().optional(),
  // Prev-period customer-charge total (usage_charges basis, cents) — the
  // revenue-basis sibling of prevCostCents above (which is COGS/`costs`
  // table). Backs an honest Revenue/Cost-per-Analysis trend that shares its
  // basis with the current-period value instead of being paired with a COGS
  // delta (WS6-T6). Nullable: no billing permission or a fetch failure both
  // read as "no comparison available", not a fabricated 0. `.optional()`
  // tolerates pre-existing cache entries written before this field existed.
  prevChargeCents: z.number().nullable().optional(),
});

export const dashboardSparklinesSchema = z.object({
  activeProjects: z.array(z.number()).optional(),
  recordsProcessed: z.array(z.number()).optional(),
  callsAnalyzed: z.array(z.number()).optional(),
  totalUsers: z.array(z.number()).optional(),
  qualityScore: z.array(z.number().nullable()).optional(),
  monthlySpend: z.array(z.number()).optional(),
});

// WS6-T6: sourced from usage_charges (real customer charges), not `costs`
// (provider COGS) — see dashboard.ts query #9. `description` (a baked
// English string, e.g. "Asr - elevenlabs") was replaced with structured
// `recordType`/`billableMinutes` so the frontend can compose a localized
// label instead of shipping backend-formatted English text (WS6-T1
// principle: the backend never emits already-localized strings).
export const dashboardTransactionSchema = z.object({
  id: z.string(),
  type: z.literal('usage'),
  recordType: z.enum(['call', 'chat']),
  // Always 0 for chats (flat per-unit billing, not minute-denominated) —
  // see per-minute-charge.ts.
  billableMinutes: z.number().nonnegative(),
  amount: z.number(),
  // Nullable: a charge row with no createdAt has no honest timestamp to
  // report — the backend sends null rather than fabricating "now".
  timestamp: z.string().nullable(),
});

export const dashboardCategoryDistributionItemSchema = z.object({
  category: z.string(),
  count: z.number().int().nonnegative(),
  avgScore: z.number().nullable(),
});

// ---- adminOps (admin/owner roles only) ------------------------------------

export const dashboardProcessingQueueItemSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  status: z.string().nullable(),
  projectName: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const dashboardRecentErrorSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  /** The stored pipeline text — platform owner only, `null` for everyone else. */
  errorMessage: z.string().nullable(),
  /**
   * Why the record failed. Optional on purpose: edge-api deploys first and may
   * be rolled back alone; the UI must still parse an answer without it.
   */
  reason: processingErrorReasonSchema.optional(),
  updatedAt: z.string().nullable(),
  projectName: z.string().nullable(),
});

export const dashboardErrorDistributionItemSchema = z.object({
  stage: z.string(),
  count: z.number().int().nonnegative(),
});

export const dashboardPipelineStageSchema = z.object({
  stage: z.string(),
  count: z.number().int().nonnegative(),
  avgSeconds: z.number().nonnegative(),
});

/**
 * Per-stage integration health (WS5-T5) — real failed/total counts from
 * stage_statuses, not a fabricated per-provider status. A stage absent from
 * the window entirely is simply not in this array; the UI must render that
 * as "no data", never as "healthy".
 */
export const dashboardProviderHealthItemSchema = z.object({
  stage: z.string(),
  total: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** The stored pipeline text — platform owner only, `null` for everyone else. */
  lastError: z.string().nullable(),
  /** Reason behind `lastError`; optional for the same rollback reason as `reason`. */
  lastReason: processingErrorReasonSchema.nullable().optional(),
  lastErrorAt: z.string().nullable(),
});

/**
 * Failed-record distribution by ingestion source (WS5-T6). `source` is the
 * CRM installation vendor the record came through ('bitrix24' | 'uspacy');
 * records with no installation (CDR/CSV import, manual upload, demo seed
 * data) are grouped under 'other'. A source absent from the window is simply
 * not in this array — never a fabricated zero-count entry.
 */
export const dashboardErrorSourceItemSchema = z.object({
  source: z.string(),
  count: z.number().int().nonnegative(),
});

export const dashboardAdminOpsSchema = z.object({
  processingQueue: z.array(dashboardProcessingQueueItemSchema),
  recentErrors: z.array(dashboardRecentErrorSchema),
  errorDistribution: z.array(dashboardErrorDistributionItemSchema),
  throughput: z.object({
    total: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  pipelineStages: z.array(dashboardPipelineStageSchema),
  // Optional: tolerates cache entries written before this field existed
  // (cache key v3, WS5-T4) — same "always sent live, optional for old
  // fixtures/cache" contract as costBreakdown/revenueChart below.
  providerHealth: z.array(dashboardProviderHealthItemSchema).optional(),
  // Optional for the same reason: added after cache key v3.
  errorsBySource: z.array(dashboardErrorSourceItemSchema).optional(),
  // Age of the oldest record still in an active (pending/processing) status,
  // regardless of the selected period window — queue depth is a "right now"
  // concern, not a historical one (WS5-T6). Null when nothing is queued.
  // Optional for the same pre-existing-cache-entry reason as the two fields
  // above.
  oldestQueuedAt: z.string().nullable().optional(),
});

// ---- supervisorData (supervisor/admin/owner roles) -------------------------

export const dashboardCallDistributionSchema = z.object({
  byDirection: z.array(
    z.object({ direction: z.string(), count: z.number().int().nonnegative() })
  ),
  byHour: z.array(
    z.object({
      hour: z.number().int().min(0).max(23),
      count: z.number().int().nonnegative(),
    })
  ),
  byType: z.array(z.object({ type: z.string(), count: z.number().int().nonnegative() })),
  avgDurationSeconds: z.number().nonnegative(),
  totalCount: z.number().int().nonnegative(),
});

export const dashboardAgentWorkTimeItemSchema = z.object({
  /**
   * The person's key (#1927): an Aelo user id, `<installation>:<id>` of a CRM
   * employee without an account, or the bare `agent_id`. Optional only so a UI
   * released before the edge-api keeps parsing a response from the older one.
   */
  key: z.string().optional(),
  agentId: z.string(),
  /** The installation of a CRM employee without an account; `null` otherwise. */
  installationId: z.string().nullable().optional(),
  agentName: z.string().nullable(),
  recordCount: z.number().int().nonnegative(),
  totalDurationSeconds: z.number().int().nonnegative(),
});

export const dashboardProjectSummaryItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  recordCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  lastActivity: z.string().nullable(),
});

/**
 * A "call to review" — flagged for supervisor attention because the LLM set
 * attentionFlag OR the quality score is below 60 (WS4-T2). qualityScore is
 * nullable: a call can be attentionFlag-only with no comparable score.
 */
export const dashboardCallToReviewItemSchema = z.object({
  recordId: z.string(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  qualityScore: z.number().nullable(),
  attentionFlag: z.boolean(),
  durationSeconds: z.number().int().nonnegative().nullable(),
  createdAt: z.string().nullable(),
});

export const dashboardSupervisorDataSchema = z.object({
  // Absent for a narrowed access grant without `aggregates.flow` (E1.5, #1336);
  // the lists below are then `[]` where the grant does not cover them.
  callDistribution: dashboardCallDistributionSchema.optional(),
  agentWorkTime: z.array(dashboardAgentWorkTimeItemSchema),
  projectsSummary: z.array(dashboardProjectSummaryItemSchema),
  callsToReview: z.array(dashboardCallToReviewItemSchema),
  // True when the window held more analyses than the review-candidate fetch cap
  // (CR-08): the worst-first list then only ranked the newest slice, so an older
  // low score can drop off silently — the Overview tab shows a muted "worst among
  // the most recent" note. `.optional()` tolerates KV cache entries written
  // before this field existed (treated as false); the live route always sends it.
  callsToReviewTruncated: z.boolean().optional(),
});

// ---- config events (model-drift markers on quality trends, WS7-T14) --------

/**
 * A moment the analysis configuration changed, overlaid as a vertical marker
 * on quality-trend charts so a manager doesn't read a score jump that
 * coincides with a config change as a fact about their team.
 *
 * `kind` is `'promptProfile'` — an org edited an analysis prompt profile (one
 * immutable row of the `prompt_profile_change_events` journal). The quality
 * charts get only that kind. `GET /analytics/config-events?include=voice` adds
 * the «Голос клиента» kinds when the section's gate is open (#2394, spec
 * `docs/plans/2026-10-18-voc-trends-read.md` §4): `model` / `codesVersion` — the
 * first current analysis with a new model id / code-set version (label = the
 * new value; the day is the analysis' own date, not the fact's), `analysisConfig`
 * — an edit of the org's analysis config (`analysis_client_config_changes`,
 * label empty), `themeSet` — a change of the conversation-theme set
 * (`org_theme_changes`, label empty: a theme name is the customer's text).
 *
 * `date` is the ISO day (YYYY-MM-DD) the change is datable to — in the viewer's
 * zone when the request names one (`timeZone`), else by `tzOffset`. `label` is
 * the profile name or the new value for the tooltip. A profile's journal reports
 * every in-window edit at its real date; a full diff of the change is out of scope.
 */
export const dashboardConfigEventSchema = z.object({
  date: z.string(),
  kind: z.enum(['promptProfile', 'model', 'codesVersion', 'analysisConfig', 'themeSet']),
  label: z.string(),
});

export const dashboardConfigEventsResponseSchema = z.object({
  configEvents: z.array(dashboardConfigEventSchema),
  /**
   * The markers of a locked org's demo (#2816), answered with the demo series
   * of Voice of Customer: the customer's own journals are not read.
   */
  demoOnly: lockedDemoOnlySchema,
});

// ---- aiAccuracy (admin/owner roles) ----------------------------------------

/**
 * "AI Accuracy" (WS7-T9) — how agents rate the AI's analysis of their calls
 * (1–5 stars, `records_metadata.agentRating`). The only metric in the product
 * anchored to a human judgement rather than the model's own opinion.
 *
 * - `averageRating` is null only when the window carried no ratings at all.
 * - `ratingCount` is always shown next to the average so a small sample reads
 *   as "3 ratings", not a confident verdict.
 * - `disagreementShare` (fraction rated ≤2) is null until the sample is large
 *   enough to be meaningful — a single 1-star must not surface as "100%
 *   disagreement". The backend applies that threshold; the UI shows the share
 *   only when it is non-null.
 */
export const dashboardAiAccuracySchema = z.object({
  averageRating: z.number().nullable(),
  ratingCount: z.number().int().nonnegative(),
  disagreementShare: z.number().min(0).max(1).nullable(),
});

// ---- complianceResults (admin/owner roles) ---------------------------------

/**
 * "Compliance Results" (WS7-T8) — the org-level pass-rate of the three
 * compliance checks the analysis pipeline already evaluates per call
 * (recording disclaimer, TCPA checklist, data privacy). WS2 removed the
 * *fabricated* compliance percentages as a regulatory risk; these are the real
 * pass/fail verdicts, now aggregated.
 *
 * This is an AI assessment of the transcript, NOT a legal determination — the
 * card renders a permanent disclaimer and never asserts "you are compliant".
 *
 * - `passRate` per check is null until at least one call evaluated that check.
 * - `evaluatedCalls` (n, the denominator — only calls that carried a compliance
 *   section) is shown on the card; when it is 0 the UI renders an empty state,
 *   never a fabricated 100%.
 * - `truncated` is true when the window held more evaluated calls than the scan
 *   cap, so the pass-rate is over the most recent sample.
 */
export const dashboardComplianceCheckSchema = z.object({
  key: z.enum(['recordingDisclaimer', 'tcpaCompliance', 'dataPrivacy']),
  passedCount: z.number().int().nonnegative(),
  evaluatedCount: z.number().int().nonnegative(),
  passRate: z.number().min(0).max(1).nullable(),
  /**
   * Э2 встроенных критериев (#2517): встроенный этой проверки сейчас на паузе
   * каталога Aelo (`catalog`, до повторного замера) — у новых записей
   * результата нет, и
   * карточка объясняет паузу, а не показывает пустоту. Необязательное: без
   * поля карточка ведёт себя как до Э2.
   */
  pausedByAelo: z.boolean().optional(),
});

export const dashboardComplianceResultsSchema = z.object({
  checks: z.array(dashboardComplianceCheckSchema),
  evaluatedCalls: z.number().int().nonnegative(),
  truncated: z.boolean(),
});

// ============================================================================
// GET /api/v1/dashboard response
// ============================================================================

export const dashboardResponseSchema = z.object({
  period: dashboardPeriodSchema,
  periodDays: z.number().int().positive(),
  budget: dashboardBudgetSchema,
  // Absent for a narrowed access grant without `aggregates.flow` (E1.5, #1336).
  // A list a grant does not cover is sent as `[]`.
  pipelineStats: dashboardPipelineStatsSchema.optional(),
  recentRecords: z.array(dashboardRecentRecordSchema),
  recentAlerts: z.array(dashboardRecentAlertSchema),
  issues: z.array(dashboardIssueSchema),
  recordsChart: z.array(dashboardRecordsChartItemSchema),
  // Provider COGS by provider and by cost type: the platform owner only
  // (#1148); both are absent for everyone else (#1177).
  costChart: z.array(dashboardCostChartItemSchema).optional(),
  costBreakdown: z.array(dashboardCostBreakdownItemSchema).optional(),
  // Always sent live; `.optional()` only tolerates pre-existing
  // DashboardResponse-shaped fixtures (WS5-T4). revenueChart is billing-gated (empty
  // when canViewBilling is false); recordsChartByType is not RBAC-gated
  // (same access as recordsChart) but is new alongside it.
  revenueChart: z.array(dashboardRevenueChartItemSchema).optional(),
  recordsChartByType: z.array(dashboardRecordsChartByTypeItemSchema).optional(),
  metrics: dashboardMetricsSchema,
  sparklines: dashboardSparklinesSchema,
  // Real-date quality trend (WS7-T14). Always sent live; `.optional()` only
  // tolerates pre-existing DashboardResponse fixtures that predate this field.
  qualityScoreChart: z.array(dashboardQualityTrendItemSchema).optional(),
  recentTransactions: z.array(dashboardTransactionSchema),
  adminOps: dashboardAdminOpsSchema.optional(),
  supervisorData: dashboardSupervisorDataSchema.optional(),
  // AI Accuracy card (WS7-T9), admin/owner roles only — undefined otherwise,
  // same role-gated-optional contract as adminOps/supervisorData above.
  aiAccuracy: dashboardAiAccuracySchema.optional(),
  // Compliance Results card (WS7-T8), admin/owner roles only — undefined
  // otherwise, same role-gated-optional contract as aiAccuracy above.
  complianceResults: dashboardComplianceResultsSchema.optional(),
  categoryDistribution: z.array(dashboardCategoryDistributionItemSchema),
  warnings: z.array(z.string()).optional(),
});

// ============================================================================
// Types
// ============================================================================

export type DashboardPeriod = z.infer<typeof dashboardPeriodSchema>;
export type DashboardBudget = z.infer<typeof dashboardBudgetSchema>;
export type DashboardPipelineStats = z.infer<typeof dashboardPipelineStatsSchema>;
export type DashboardRecentRecordTitle = z.infer<typeof dashboardRecentRecordTitleSchema>;
export type DashboardRecentRecord = z.infer<typeof dashboardRecentRecordSchema>;
export type DashboardPrimaryAlertEvidence = z.infer<
  typeof dashboardPrimaryAlertEvidenceSchema
>;
export type DashboardRecentAlert = z.infer<typeof dashboardRecentAlertSchema>;
export type DashboardIssue = z.infer<typeof dashboardIssueSchema>;
export type DashboardRecordsChartItem = z.infer<typeof dashboardRecordsChartItemSchema>;
export type DashboardQualityTrendItem = z.infer<typeof dashboardQualityTrendItemSchema>;
export type DashboardCostChartItem = z.infer<typeof dashboardCostChartItemSchema>;
export type DashboardRevenueChartItem = z.infer<typeof dashboardRevenueChartItemSchema>;
export type DashboardRecordsChartByTypeItem = z.infer<
  typeof dashboardRecordsChartByTypeItemSchema
>;
export type DashboardCostBreakdownItem = z.infer<typeof dashboardCostBreakdownItemSchema>;
export type DashboardMetrics = z.infer<typeof dashboardMetricsSchema>;
export type DashboardSparklines = z.infer<typeof dashboardSparklinesSchema>;
export type DashboardTransaction = z.infer<typeof dashboardTransactionSchema>;
export type DashboardCategoryDistributionItem = z.infer<
  typeof dashboardCategoryDistributionItemSchema
>;
export type DashboardAiAccuracy = z.infer<typeof dashboardAiAccuracySchema>;
export type DashboardComplianceCheck = z.infer<typeof dashboardComplianceCheckSchema>;
export type DashboardComplianceResults = z.infer<typeof dashboardComplianceResultsSchema>;
export type DashboardProcessingQueueItem = z.infer<
  typeof dashboardProcessingQueueItemSchema
>;
export type DashboardRecentError = z.infer<typeof dashboardRecentErrorSchema>;
export type DashboardErrorDistributionItem = z.infer<
  typeof dashboardErrorDistributionItemSchema
>;
export type DashboardPipelineStage = z.infer<typeof dashboardPipelineStageSchema>;
export type DashboardProviderHealthItem = z.infer<
  typeof dashboardProviderHealthItemSchema
>;
export type DashboardErrorSourceItem = z.infer<typeof dashboardErrorSourceItemSchema>;
export type DashboardAdminOps = z.infer<typeof dashboardAdminOpsSchema>;
export type DashboardCallDistribution = z.infer<typeof dashboardCallDistributionSchema>;
export type DashboardAgentWorkTimeItem = z.infer<typeof dashboardAgentWorkTimeItemSchema>;
export type DashboardProjectSummaryItem = z.infer<
  typeof dashboardProjectSummaryItemSchema
>;
export type DashboardCallToReviewItem = z.infer<typeof dashboardCallToReviewItemSchema>;
export type DashboardSupervisorData = z.infer<typeof dashboardSupervisorDataSchema>;
export type DashboardConfigEvent = z.infer<typeof dashboardConfigEventSchema>;
export type DashboardConfigEventsResponse = z.infer<
  typeof dashboardConfigEventsResponseSchema
>;
export type DashboardResponse = z.infer<typeof dashboardResponseSchema>;
